/**
 * BrokenStreakDrama Component
 * ===========================
 * A highly dramatic, Duolingo-inspired animation sequence that plays
 * when a user loses their savings streak.
 * 
 * Features:
 * - Smooth screen dimming
 * - Bouncy shake animation on dashboard card
 * - Sad/exhausted Guapo mascot (Tamagotchi effect)
 * - Spring-based mounting/unmounting
 * - "Try Again" dismissal with database update
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiPut } from '@/utils/api';
import { GuapoMascot } from '@/components/GuapoMascot';

export function BrokenStreakDrama({ 
  isVisible, 
  brokenStreakDays = 0,
  onDismiss 
}) {
  const [isExiting, setIsExiting] = useState(false);
  const [showGuapo, setShowGuapo] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Delay Guapo appearance for dramatic effect
      const timer = setTimeout(() => setShowGuapo(true), 400);
      return () => clearTimeout(timer);
    } else {
      setShowGuapo(false);
    }
  }, [isVisible]);

  const handleTryAgain = async () => {
    setIsExiting(true);
    
    // Update database to clear the drama flag
    try {
      await apiPut('/user/clear-streak-drama', {});
    } catch (error) {
      console.error('Error clearing streak drama:', error);
    }
    
    // Wait for exit animation
    setTimeout(() => {
      onDismiss();
      setIsExiting(false);
    }, 500);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Dimmed Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="fixed inset-0 bg-black/80 z-[100] backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Main Drama Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: isExiting ? 0 : 1, 
              scale: isExiting ? 0.8 : 1 
            }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 25,
              duration: 0.5 
            }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
          >
            <div className="relative max-w-sm w-full">
              
              {/* Shaking Card Effect */}
              <motion.div
                animate={showGuapo ? {
                  x: [0, -10, 10, -10, 10, -5, 5, 0],
                  rotate: [0, -2, 2, -2, 2, -1, 1, 0]
                } : {}}
                transition={{ 
                  duration: 0.6, 
                  ease: "easeInOut",
                  delay: 0.2
                }}
                className="bg-gradient-to-b from-[#1a1a2e] to-[#16213e] rounded-3xl overflow-hidden shadow-2xl border border-red-500/30"
              >
                {/* Red Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-b from-red-500/10 to-transparent pointer-events-none" />
                
                {/* Broken Streak Header */}
                <div className="relative px-6 pt-8 pb-4 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: showGuapo ? 1 : 0 }}
                    transition={{ 
                      type: "spring", 
                      stiffness: 400, 
                      damping: 15,
                      delay: 0.3 
                    }}
                    className="inline-block mb-4"
                  >
                    {/* Broken Fire Icon */}
                    <div className="relative">
                      <span className="text-6xl filter grayscale opacity-50">🔥</span>
                      <motion.div
                        initial={{ scale: 0, rotate: -45 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.6, type: "spring" }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <span className="text-4xl">💔</span>
                      </motion.div>
                    </div>
                  </motion.div>
                  
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: showGuapo ? 1 : 0, y: showGuapo ? 0 : 20 }}
                    transition={{ delay: 0.5, duration: 0.3 }}
                    className="text-2xl font-bold text-white mb-2"
                  >
                    Streak Lost
                  </motion.h2>
                  
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: showGuapo ? 1 : 0 }}
                    transition={{ delay: 0.6 }}
                    className="text-gray-400 text-sm"
                  >
                    {brokenStreakDays > 0 
                      ? `Your ${brokenStreakDays}-day streak has ended`
                      : "You missed your savings goal today"
                    }
                  </motion.p>
                </div>

                {/* Sad Guapo Section */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: showGuapo ? 1 : 0, y: showGuapo ? 0 : 30 }}
                  transition={{ 
                    delay: 0.4, 
                    type: "spring", 
                    stiffness: 200 
                  }}
                  className="relative px-6 py-6 flex flex-col items-center"
                >
                  {/* Guapo with shake + grayscale (CSS-driven) */}
                  <div className="relative mb-4">
                    <GuapoMascot
                      mode="broken"
                      size="lg"
                      data-testid="guapo-broken-streak"
                    />

                    {/* Speech Bubble */}
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.8, type: "spring" }}
                      className="absolute -top-2 -right-4 bg-white rounded-xl px-3 py-2 shadow-lg"
                    >
                      <span className="text-sm font-medium text-gray-800">
                        Don't give up!
                      </span>
                      <div className="absolute -bottom-2 left-4 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-white" />
                    </motion.div>
                  </div>

                  {/* Motivational Message */}
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                    className="text-center text-gray-300 text-sm mb-6 max-w-xs"
                  >
                    Every savings journey has bumps. What matters is getting back on track. 
                    <span className="text-yellow-400 font-semibold"> You've got this!</span>
                  </motion.p>

                  {/* Try Again Button */}
                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1, type: "spring" }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleTryAgain}
                    className="w-full py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold text-lg rounded-2xl shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-shadow"
                    data-testid="try-again-btn"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span>🔥</span>
                      Start a New Streak
                      <span>🔥</span>
                    </span>
                  </motion.button>
                </motion.div>

                {/* Bottom Decoration */}
                <div className="h-2 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500" />
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default BrokenStreakDrama;
